// src/oura/components/docs/write/ShareDonePanel.tsx
//
// The doc drawer's "Sharing and Done" fold for a file Oura shares but does not write:
// a client PDF (3 Nov 2026). A Google Doc has the full "Questions and sharing"
// fold (DocWritePanel); a PDF has no comments for Oura to read or write, so it
// gets the two steps that still apply:
//
//   Share with the client   the same Share view a Google Doc opens
//   Mark as done / Undo     the same dialog as the board row (MarkDoneDialog)
//
// Rendered only for a doc `canShare` allows and `canWrite` does not.

import { useEffect, useState } from "react";
import { CheckIcon } from "lucide-react";
import { markDocumentDone, type StaffDocument } from "@service/aeo/clientDocs";
import { DrawerFold, Dot, ListRow, PillButton, TYPE } from "@oura/ui";
import { cn } from "../../../lib/cn";
import { docDate, staffDocTitle } from "@oura/docs";
import { MarkDoneDialog } from "../MarkDoneDialog";
import type { DocWriteView } from "./model";

export function ShareDonePanel({ doc, onView }: { doc: StaffDocument; onView: (view: DocWriteView) => void }) {
    const [marking, setMarking] = useState(false);
    const [undoing, setUndoing] = useState(false);
    const [failed, setFailed] = useState<string | null>(null);
    // Undone here before the drawer's read comes back (it re-reads on every documents write): the panel
    // shows the doc as not Done at once, so Undo cannot be pressed twice (Jack). The next read settles it.
    const [undone, setUndone] = useState(false);
    const doneAt = doc.done?.at ?? null;
    useEffect(() => setUndone(false), [doneAt]);
    const shared = doc.sharing.clientCanSee;
    const done = undone ? null : (doc.done ?? null);
    const sharedLine = shared ? (docDate(doc.sharing.sharedAt) ? `Shared ${docDate(doc.sharing.sharedAt)}` : "Shared") : "Not shared yet";

    // Undo is one click, as on the board row: no day to ask.
    const undo = async () => {
        setUndoing(true);
        setFailed(null);
        const r = await markDocumentDone(doc.id, false);
        setUndoing(false);
        if (r.ok) setUndone(true);
        else setFailed("That did not go through. Try again.");
    };

    return (
        <DrawerFold
            id="staff-doc.share-done"
            label="Sharing and Done"
            note={done ? `Done ${docDate(done.at) ?? ""}`.trim() : sharedLine}
            remember
            defaultOpen
        >
            <div>
                <ListRow
                    lead="dot"
                    icon={<Dot tone={shared ? "green" : "blue"} />}
                    title="Share with the client"
                    meta={sharedLine}
                    onClick={() => onView("share")}
                    stackMeta
                />
            </div>
            {/* Where the backend serves `done` (#4541), as the board row has it. */}
            {doc.done !== undefined && (
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {done ? (
                        <>
                            <span className={cn(TYPE.tc, "text-gray-700")}>{`Done ${docDate(done.at) ?? ""} by ${done.by}`.replace("  ", " ")}</span>
                            <PillButton size="sm" variant="text" busy={undoing} onClick={() => void undo()}>
                                Undo
                            </PillButton>
                        </>
                    ) : (
                        <PillButton size="sm" icon={CheckIcon} onClick={() => setMarking(true)}>
                            Mark as done
                        </PillButton>
                    )}
                    {failed && (
                        <span role="alert" className={cn(TYPE.tc, "text-rose-600")}>
                            {failed}
                        </span>
                    )}
                </div>
            )}
            <MarkDoneDialog doc={doc} title={staffDocTitle(doc)} open={marking} onClose={() => setMarking(false)} />
        </DrawerFold>
    );
}
